<script setup lang="ts">
import {ref, watch} from "vue";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/core/components/ui/dialog";
import {Textarea} from "@/core/components/ui/textarea";
import {Label} from "@/core/components/ui/label";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {LegacyProduct} from "@/core/interfaces/Product";
import {supportProgramApi} from "@/container";

const props = defineProps<{
    supportProgramId: string | number;
}>();

const emits = defineEmits<{
    (e: 'created'): void;
}>();

const open = ref(false);
const selectedProduct = ref<LegacyProduct | null>(null);
const saving = ref(false);
const comments = ref('');

watch(open, (value) => {
    if (value) {
        selectedProduct.value = null;
        comments.value = '';
    }
});

const save = () => {
    if (!selectedProduct.value) {
        return;
    }

    saving.value = true;

    supportProgramApi()
        .addProductRecommendation(props.supportProgramId, {
            product_id: selectedProduct.value.id,
            comments: comments.value || null
        })
        .then(() => {
            emits('created');
            open.value = false;
        })
        .finally(() => {
            saving.value = false;
        });
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <BaseButton primary density="comfortable">
        Ajouter un produit conseil
      </BaseButton>
    </DialogTrigger>
    <DialogContent class="flex flex-col max-h-[85dvh] overflow-y-auto" @interact-outside="(e) => e.preventDefault()">
      <DialogHeader class="p-0">
        <DialogTitle>
          Ajouter un produit conseil au programme
        </DialogTitle>
      </DialogHeader>
      <div class="space-y-4">
        <div class="space-y-1">
          <Label>Produit</Label>
          <BaseProductSearchBar @select="(product) => selectedProduct = product" />
        </div>
        <div class="space-y-1">
          <Label for="recommendation-comments">Commentaire</Label>
          <Textarea id="recommendation-comments" v-model="comments" placeholder="Commentaire sur le produit conseil" />
        </div>
      </div>
      <DialogFooter>
        <BaseButton
          density="comfortable"
          primary
          :loading="saving"
          :disabled="!selectedProduct"
          @click="save"
        >
          Ajouter le produit conseil
        </BaseButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
